"use client";
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
    Phone,
    Mail,
    ChevronDown,
    Menu,
    X,
    Globe,
    ArrowRight
} from 'lucide-react';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const countries = [
        { code: 'IN', url: '/' },
        { code: 'BHR', url: '/' },
        { code: 'KW', url: '/' },
        { code: 'OM', url: '/' },
        { code: 'SA', url: '/' },
        { code: 'UAE', url: '/' },
    ];

    const productLinks = [
        { name: 'Autonet DMS', href: '/products/autonet-dms', desc: 'Enterprise Dealership Management' },
        { name: 'Autonext DMS', href: '/products/autonext-dms', desc: 'Cloud-based Solutions' },
        { name: 'Smart Body Shop ERP', href: '/products/smart-body-shop-erp', desc: 'Advanced Body Shop Management' },
        { name: 'MyPaper', href: '/products/mypaper', desc: 'Digital Documentation' },
    ];

    return (
        <header className="fixed w-full top-0 z-50 transition-all duration-300">

            {/* 1. TOP BAR - Brand Color Background */}
            <div className={`bg-brand py-2 text-white transition-all duration-300 overflow-hidden ${scrolled ? 'h-0 opacity-0' : 'h-10 opacity-100'
                }`}>
                <div className="container mx-auto px-6 flex justify-between items-center h-full">
                    <div className="flex items-center gap-6">
                        <a href="tel:+919011060932" className="hover:text-white/80 flex items-center gap-2 transition text-xs font-medium">
                            <Phone size={14} className="text-white" /> +91 9011060932
                        </a>
                        <a href="mailto:sales@emsys.co.in" className="hidden sm:flex hover:text-white/80 items-center gap-2 transition text-xs font-medium">
                            <Mail size={14} className="text-white" /> sales@emsys.co.in
                        </a>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-[10px] tracking-wider font-bold">
                            <Globe size={12} className="text-white" />
                            {countries.map((c) => (
                                <Link key={c.code} href={c.url} prefetch={false} className="hover:text-white/70 px-1 border-r last:border-0 border-white/30 transition-colors">
                                    {c.code}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. MAIN NAVIGATION */}
            <nav className={`transition-all duration-500 ${scrolled
                ? 'mx-auto mt-2 max-w-[95%] rounded-2xl shadow-xl bg-white/90 backdrop-blur-md border border-slate-200/50 py-2'
                : 'bg-white border-b border-slate-100 py-4'
                }`}>
                <div className="container mx-auto px-6 flex items-center justify-between">

                    {/* LOGO */}
                    <Link href="/" prefetch={false} className="relative w-40 h-12">
                        <Image
                            src="/images/emsysnew-logo.png"
                            alt="Emsys Logo"
                            fill
                            className="object-contain"
                            priority
                        />
                    </Link>

                    {/* DESKTOP NAV */}
                    <div className="hidden lg:flex items-center gap-10">
                        <div className="flex items-center gap-8 text-[14px] font-semibold text-slate-700">
                            <Link href="/" prefetch={false} className="hover:text-brand transition-colors relative group">
                                Home
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand transition-all group-hover:w-full"></span>
                            </Link>
                            <Link href="/about" prefetch={false} className="hover:text-brand transition-colors relative group">
                                About
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand transition-all group-hover:w-full"></span>
                            </Link>

                            {/* PRODUCTS MEGA DROPDOWN */}
                            <div className="relative group cursor-pointer py-2">
                                <span className="flex items-center gap-1 group-hover:text-brand transition-colors">
                                    Products <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />
                                </span>

                                <div className="absolute top-[120%] left-1/2 -translate-x-1/2 w-[480px] bg-white shadow-2xl rounded-2xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible translate-y-4 group-hover:translate-y-0 transition-all duration-300 p-6 grid grid-cols-2 gap-4">
                                    {productLinks.map((link) => (
                                        <Link
                                            key={link.name}
                                            href={link.href}
                                            prefetch={false}
                                            className="group/item flex flex-col p-3 rounded-xl hover:bg-slate-50 transition-all"
                                        >
                                            <span className="font-bold text-slate-900 group-hover/item:text-brand flex items-center gap-2">
                                                {link.name} <ArrowRight size={12} className="opacity-0 group-hover/item:opacity-100 -translate-x-2 group-hover/item:translate-x-0 transition-all" />
                                            </span>
                                            <span className="text-xs text-slate-500 font-normal">{link.desc}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            <Link href="/gallery" prefetch={false} className="hover:text-brand transition-colors relative group">
                                Gallery
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand transition-all group-hover:w-full"></span>
                            </Link>
                            <Link href="/contact" prefetch={false} className="hover:text-brand transition-colors relative group">
                                Contact
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand transition-all group-hover:w-full"></span>
                            </Link>
                        </div>

                        {/* CTA BUTTON */}
                        <Link href="/contact" prefetch={false} className="bg-brand text-white px-6 py-2.5 rounded-xl text-xs font-bold tracking-widest hover:bg-brand/90 hover:shadow-lg hover:shadow-brand/30 transition-all active:scale-95">
                            Get Quote
                        </Link>
                    </div>


                    {/* MOBILE TOGGLE */}
                    <button
                        onClick={() => setIsMenuOpen(true)}
                        aria-label="Open navigation menu"
                        className="lg:hidden p-2 text-slate-900 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                        <Menu size={24} />
                    </button>
                </div>
            </nav>

            {/* 3. MOBILE MENU SIDEBAR */}
            <div className={`fixed inset-0 z-[100] ${isMenuOpen ? 'visible' : 'invisible'}`}>
                <div
                    className={`absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-500 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
                    onClick={() => setIsMenuOpen(false)}
                />
                <div className={`absolute right-0 top-0 h-full w-[300px] bg-white shadow-2xl transition-transform duration-500 ease-out p-8 flex flex-col ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>

                    <div className="flex items-center justify-between mb-10">
                        <div className="relative w-32 h-10">
                            <Image
                                src="/images/emsysnew-logo.png"
                                alt="Emsys Logo"
                                fill
                                className="object-contain"
                            />
                        </div>
                        <button aria-label="Close navigation menu" className="p-2 text-slate-400 hover:text-slate-900 transition-colors" onClick={() => setIsMenuOpen(false)}>
                            <X size={28} />
                        </button>
                    </div>

                    <div className="space-y-6">
                        <Link href="/" prefetch={false} className="block text-xl font-bold text-slate-900" onClick={() => setIsMenuOpen(false)}>Home</Link>
                        <Link href="/about" prefetch={false} className="block text-xl font-bold text-slate-900" onClick={() => setIsMenuOpen(false)}>About</Link>
                        <Link href="/gallery" prefetch={false} className="block text-xl font-bold text-slate-900" onClick={() => setIsMenuOpen(false)}>Gallery</Link>
                        <Link href="/contact" prefetch={false} className="block text-xl font-bold text-slate-900" onClick={() => setIsMenuOpen(false)}>Contact</Link>

                        <div className="pt-6 border-t border-slate-100 space-y-4">
                            <p className="text-[10px] font-black tracking-widest text-brand">Our Products</p>
                            {productLinks.map(link => (
                                <Link key={link.name} href={link.href} prefetch={false} className="block text-lg font-semibold text-slate-700 hover:text-brand" onClick={() => setIsMenuOpen(false)}>
                                    {link.name}
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div className="mt-auto space-y-4">
                        <Link href="/contact" prefetch={false} className="flex items-center justify-center gap-2 bg-brand text-white w-full py-4 rounded-xl font-bold shadow-lg" onClick={() => setIsMenuOpen(false)}>
                            Quick Enquiry <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;